import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Icon, Loader, Tooltip, Typography } from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { getPreLaunchDetail } from '../../../redux/prelaunch/action';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';

import './PreLaunchDetail.css';

const DATE_COL_PATTERN = /(_date|_at|date|time)$/i;

const HIDDEN_COLS = /^(indexed_at|metric_type|.*_id|.*_key|id|key)$/i;

const TRUST_JUSTIFICATION_COL = 'trust_justification';

function formatCellValue(col, value, globalDateFormat) {
  if (value == null || value === '') return '-';

  if (DATE_COL_PATTERN.test(col)) {
    const m = moment.utc(value).local();

    if (m.isValid())
      return m.format(
        `${(globalDateFormat || 'MMM DD, YYYY').toUpperCase()} hh:mm:ss`
      );
  }

  return value;
}

function TrustJustificationCell({ value }) {
  const intl = useIntl();

  let items = [];

  try {
    items = typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return <span>{value}</span>;
  }

  if (!Array.isArray(items) || items.length === 0) return <span>-</span>;

  return (
    <div className='PreLaunchDetail-trustJustification'>
      {items.map((item) => {
        const key = Object.keys(item)[0];
        const vals =
          item[key]?.map((v) => (Number.isNaN(Number(v)) ? v : Number(v).toFixed(2))) ||
          [];

        return (
          <div key={key} className='PreLaunchDetail-trustJustificationItem'>
            <Icon
              kind='AlertInfo'
              size='small'
              color='neutral-900'
              className='PreLaunchDetail-trustJustificationIcon'
            />
            <Typography
              kind='body2'
              className='PreLaunchDetail-trustJustificationText'>
              {localize(intl, key, vals)}
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

TrustJustificationCell.propTypes = {
  value: PropTypes.string.isRequired,
};

function TruncatedTh({ label }) {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.offsetWidth);
    }
  }, [label]);

  return (
    <th>
      {isTruncated ? (
        <Tooltip text={label} placement='top'>
          <Box tag='span' ref={ref} className='PreLaunchDetail-thText'>
            {label}
          </Box>
        </Tooltip>
      ) : (
        <Box tag='span' ref={ref} className='PreLaunchDetail-thText'>
          {label}
        </Box>
      )}
    </th>
  );
}

TruncatedTh.propTypes = {
  label: PropTypes.string.isRequired,
};

function PreLaunchDetail({ metric, page, pageSize }) {
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const { detailData, isDetailLoading, detailError, reportData } = useSelector(
    (state) => state.certifications.prelaunch
  );
  const { ecmDateFormat: globalDateFormat } = useSelector(
    (state) => state?.user?.headerMenu
  );

  useEffect(() => {
    if (!reportData?.reportId || !metric) return;
    dispatch(
      getPreLaunchDetail({
        reportId: reportData.reportId,
        metric,
        page,
        size: pageSize,
      })
    );
  }, [dispatch, reportData?.reportId, metric, page, pageSize]);

  const records = detailData?.records || [];
  const columns =
    records.length > 0
      ? Object.keys(records[0]).filter((col) => !HIDDEN_COLS.test(col))
      : [];
  const columnLabels = detailData?.columnLabels || {};
  const toTitleCase = (str) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const formatColLabel = (col) =>
    columnLabels[col] ? toTitleCase(columnLabels[col]) : toTitleCase(col);

  return (
    <Box className='PreLaunchDetail'>
      {isDetailLoading && (
        <Box className='PreLaunchDetail-loading'>
          <Loader kind='dots' format='inline' size='medium' color='brand' />
          {l('Loading…')}
        </Box>
      )}

      {!isDetailLoading && detailError && (
        <Box className='PreLaunchDetail-errorState'>
          <Typography kind='body2'>{detailError}</Typography>
        </Box>
      )}

      {!isDetailLoading && !detailError && records.length === 0 && (
        <Box className='PreLaunchDetail-empty'>{l('No records found.')}</Box>
      )}

      {!isDetailLoading && !detailError && records.length > 0 && (
        <Box className='PreLaunchDetail-tableWrap'>
          <table className='PreLaunchDetail-table'>
            <thead>
              <tr>
                {columns.map((col) => (
                  <TruncatedTh key={col} label={formatColLabel(col)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((row, rowIdx) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIdx}>
                  {columns.map((col) => (
                    <td key={col}>
                      {col === TRUST_JUSTIFICATION_COL ? (
                        <TrustJustificationCell value={row[col]} />
                      ) : (
                        formatCellValue(col, row[col], globalDateFormat)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
}

PreLaunchDetail.propTypes = {
  metric: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number,
};

PreLaunchDetail.defaultProps = {
  pageSize: 25,
};

export default PreLaunchDetail;
